import { useState, useRef, useEffect } from "react";

const CustomDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select",
  className = "",
  width = "w-50",
  showSelectedIcon = true,
}) => {
  const [open, setOpen] = useState(false);
  const [openSubFor, setOpenSubFor] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setOpenSubFor(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const normalizedOptions = options.map((opt) =>
    typeof opt === "string"
      ? { label: opt, value: opt, icon: null, sub: undefined }
      : { icon: null, ...opt }
  );

  const findSelected = () => {
    const top = normalizedOptions.find((o) => o.value === value);
    if (top) return { label: top.label, icon: top.icon };
    for (const o of normalizedOptions) {
      if (Array.isArray(o.sub)) {
        const s = o.sub.find((so) => so.value === value);
        if (s) return { label: s.label, icon: s.icon ?? o.icon, parentLabel: o.label };
      }
    }
    return null;
  };

  const selectedObj = findSelected();

  return (
    <div className={`relative ${width} ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) setOpenSubFor(null);
        }}
        className="w-full h-12 bg-white border border-gray-300 px-3 py-2 rounded-3xl
                   flex justify-between items-center shadow-sm 
                   hover:border-gray-400 transition"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {showSelectedIcon && selectedObj?.icon && (
            <i className={`${selectedObj.icon}`}></i>
          )}

          <div
            className="truncate min-w-0"
            title={
              selectedObj
                ? `${selectedObj.label}${selectedObj.parentLabel ? ` — ${selectedObj.parentLabel}` : ""}`
                : placeholder
            }
          >
            {selectedObj ? (
              <>
                <div className="text-sm font-semibold truncate">{selectedObj.label}</div>
                {selectedObj.parentLabel && (
                  <div className="text-xs text-gray-500 truncate -mt-0.5">
                    {selectedObj.parentLabel}
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-gray-500 truncate">{placeholder}</div>
            )}
          </div>
        </div>

        <i
          className={`fa-solid fa-chevron-down text-sm transition-transform ${
            open ? "rotate-180" : ""
          }`}
        ></i>
      </button>

      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-white border rounded-md shadow-lg z-20">
          {normalizedOptions.map((opt) => {
            const hasSub = Array.isArray(opt.sub) && opt.sub.length > 0;
            const isParentOpen = openSubFor === opt.value;

            return (
              <div key={opt.value} className="relative">
                <button
                  type="button"
                  className={`flex items-center gap-2 w-full text-left px-3 py-2 capitalize hover:bg-gray-100
                    ${value === opt.value ? "bg-gray-50 font-semibold" : ""}`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setOpenSubFor(null);
                  }}
                >
                  {opt.icon && <i className={`${opt.icon}`}></i>}

                  <span className="flex-1 truncate">{opt.label}</span>

                  {hasSub && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenSubFor((prev) => (prev === opt.value ? null : opt.value));
                      }}
                      className="p-1 rounded hover:bg-gray-100"
                      aria-expanded={isParentOpen}
                      aria-label={`Open ${opt.label} submenu`}
                    >
                      <i
                        className={`fa-solid fa-chevron-right transition-transform ${
                          isParentOpen ? "rotate-90" : ""
                        }`}
                      ></i>
                    </button>
                  )}
                </button>

                {hasSub && isParentOpen && (
                  <div
                    className="absolute top-0 left-full ml-1 bg-white border rounded-md shadow-lg z-30 min-w-[10rem]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {opt.sub.map((so) => (
                      <button
                        key={so.value}
                        type="button"
                        className={`w-full px-3 py-2 text-left hover:bg-gray-100
                          ${value === so.value ? "bg-gray-50 font-semibold" : ""}`}
                        onClick={() => {
                          onChange(so.value);
                          setOpen(false);
                          setOpenSubFor(null);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          {so.icon && <i className={`${so.icon}`}></i>}
                          <span className="flex-1 truncate">{so.label}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomDropdown;
